import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import EntrarConPin from "@/components/caja/EntrarConPin";
import {
  ENLACE,
  TEXTO,
  TEXTO_DE_ERROR,
} from "@/components/caja/clasesDeLaEntrada";
import { PASARON_72_HORAS } from "@/context/caja/cajeroDeLaCaja";
import { entrarALaCaja } from "@/context/caja/entrarALaCaja";
import { useEntradasConPin } from "@/hooks/react-query/Caja/useEntradasConPin";
import { cn } from "@/lib/utils";
import type { PosEntradaConPin } from "@/platform/types";

interface EntrarALaCajaSinSesionProps {
  /** El panel está abierto: el login lo muestra en lugar del formulario. */
  abierto: boolean;
  onAbrir: () => void;
  onCerrar: () => void;
}

const nombreDeLaCaja = (e: PosEntradaConPin) =>
  [e.caja, e.negocio, e.local].filter(Boolean).join(" · ");

/**
 * La entrada a la caja sin sesión (#346 AC-2; §2.6 de OFFLINE-POS-RONDA-2.md,
 * D23 y D24). Con contraseña no se entra sin red; con el PIN, sí, si la caja
 * tiene un mandato vigente y el cajero tiene PIN en este equipo. Sólo en el
 * escritorio y sólo si hay alguna caja así.
 */
function EntrarALaCajaSinSesion({
  abierto,
  onAbrir,
  onCerrar,
}: EntrarALaCajaSinSesionProps) {
  const navigate = useNavigate();
  const { data: entradas = [] } = useEntradasConPin();
  const [elegida, setElegida] = useState<PosEntradaConPin | null>(null);

  if (entradas.length === 0) return null;

  if (!abierto) {
    return (
      <div className="dark mt-6 flex w-full flex-col items-start gap-2 border-t border-slate-700 pt-4">
        <p className="text-sm text-slate-400 text-pretty">
          ¿Sin conexión? Sin red no se entra con la contraseña, pero cada cajero
          entra a la caja con su PIN.
        </p>
        <Button type="button" variant="outline" onClick={onAbrir}>
          Entrar a la caja con PIN
        </Button>
      </div>
    );
  }

  const entrada = elegida ?? (entradas.length === 1 ? entradas[0] : null);
  const cerrar = () => {
    setElegida(null);
    onCerrar();
  };
  // Con más de una caja, volver es elegir otra.
  const volver = entradas.length > 1 ? () => setElegida(null) : cerrar;

  return (
    <div className="dark flex w-full justify-center">
      {!entrada ? (
        <section
          aria-labelledby="elegir-caja"
          className={cn("flex w-full max-w-sm flex-col gap-4", TEXTO)}
        >
          <h2 id="elegir-caja" className="text-xl font-semibold">
            ¿A qué caja entrás?
          </h2>
          <ul className="flex flex-col gap-2">
            {entradas.map((e) => (
              <li key={e.installationId}>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-10 w-full justify-start"
                  onClick={() => setElegida(e)}
                >
                  {nombreDeLaCaja(e)}
                </Button>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="link"
            className={ENLACE}
            onClick={cerrar}
          >
            Volver
          </Button>
        </section>
      ) : entrada.vencido ? (
        <section className={cn("flex w-full max-w-sm flex-col gap-4", TEXTO)}>
          <h2 className="text-xl font-semibold">{nombreDeLaCaja(entrada)}</h2>
          <p role="alert" className={cn("text-pretty", TEXTO_DE_ERROR)}>
            {PASARON_72_HORAS}
          </p>
          <Button
            type="button"
            variant="link"
            className={ENLACE}
            onClick={volver}
          >
            Volver
          </Button>
        </section>
      ) : (
        <EntrarConPin
          key={entrada.installationId}
          businessId={entrada.businessId}
          cajeros={entrada.cajeros}
          onEntrar={(cajero) => navigate(entrarALaCaja(entrada, cajero))}
          onVolver={volver}
        />
      )}
    </div>
  );
}

export default EntrarALaCajaSinSesion;
